import React from "react";
import {
  Bar,
  BarChart,
  type BarShapeProps,
  CartesianGrid,
  Rectangle,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@volca/ui/components/ui/chart";
import { cn } from "@volca/ui/lib/utils";
import { CATEGORY_PALETTE } from "@volca/ui/tokens/chart-colors";
import { useGetBusinessWeeklyStats } from "@/business/query";
import { useBusiness } from "@/core/business";
import { PARTICIPATION_PALETTE } from "@/core/colors";
import { CHART_FOCUS_CLASSES } from "./chart-focus";
import { DashboardChartCard } from "./dashboard-chart-card";

const chartConfig = {
  attributed_signup_count: {
    label: "Attributed",
    color: PARTICIPATION_PALETTE.signups.paint,
  },
  automatic_signup_count: {
    label: "Unattributed",
    color: CATEGORY_PALETTE.sand,
  },
};

const BAR_RADIUS: [number, number, number, number] = [4, 4, 0, 0];

type WeekBarProps = BarShapeProps & {
  payload?: { automatic_signup_count?: number };
};

const AttributedBar = (props: WeekBarProps) => (
  <Rectangle
    {...props}
    radius={props.payload?.automatic_signup_count ? 0 : BAR_RADIUS}
  />
);

const SignupsChartPane = React.memo(() => {
  const { businessId } = useBusiness();
  const {
    data = [],
    isLoading,
    isError,
  } = useGetBusinessWeeklyStats(businessId);
  const total = data.reduce((sum, week) => sum + week.total_signup_count, 0);

  return (
    <DashboardChartCard
      title="Sign-ups"
      total={total}
      caption={`over ${data.length} weeks`}
      legend={Object.values(chartConfig)}
      isLoading={isLoading}
      errorMessage={
        isError ? "Couldn't load sign-ups. Please try again." : undefined
      }
    >
      <ChartContainer
        config={chartConfig}
        className={cn("aspect-auto h-56 w-full", CHART_FOCUS_CLASSES)}
      >
        <BarChart
          accessibilityLayer
          data={data}
          margin={{ left: 0, right: 8, top: 12 }}
        >
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="week_range"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={(value: string) => value.split(" - ")[0]}
          />
          <YAxis
            width={32}
            tickLine={false}
            axisLine={false}
            tickMargin={4}
            allowDecimals={false}
          />
          <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
          <Bar
            dataKey="attributed_signup_count"
            stackId="signups"
            fill={chartConfig.attributed_signup_count.color}
            shape={AttributedBar}
            maxBarSize={20}
          />
          <Bar
            dataKey="automatic_signup_count"
            stackId="signups"
            fill={chartConfig.automatic_signup_count.color}
            radius={BAR_RADIUS}
            maxBarSize={20}
          />
        </BarChart>
      </ChartContainer>
    </DashboardChartCard>
  );
});

export default SignupsChartPane;
